<template>
  <BittsModalTwo
    :loading="!ready"
    title="Share with Team Members"
    :visible="true"
    class="share-report-modal"
    :width="448"
    :hide-footer="true"
    variant="data"
    @closed="onCloseModal"
  >
    <ReportAccessSelectionControls
      :hide-controls="true"
      :report-id="reportId"
      :report-creator-id="reportCreatorId"
      :existing-users="existingUsersInReport"
      class="w-full"
    />
    <footer class="flex justify-between">
      <BittsCallout
        type="neutral"
        size="x-small"
        title="Learn more about report access"
        @click="onCloseModal"
      />
      <BittsButton
        :left-icon="['fak', 'link']"
        text="Copy link"
        variant="outline"
        type="neutral"
        size="small"
        @click="onCopyReportLink"
      />
    </footer>
  </BittsModalTwo>
</template>

<script setup lang="ts">
import { BittsCallout, BittsModalTwo } from '@crossbeam/bitts';
import BittsButton from '@crossbeam/bitts/src/BittsButton/BittsButton.vue';

import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ReportAccessSelectionControls from '@/components/reports/ReportAccessSelectionControls.vue';

import useAuth from '@/composables/useAuth';
import {
  allReady,
  useFlashesStore,
  useReportsStore,
  useTeamStore,
} from '@/stores';
import { UserWithReportAccess } from '@/types/report_team_access';
import { User } from '@/types/root';
import { copyToClipBoard } from '@/utils';

const route = useRoute();
const router = useRouter();

const { currentUser } = useAuth();

const reportsStore = useReportsStore();
const teamStore = useTeamStore();
const flashesStore = useFlashesStore();
const ready = allReady(teamStore, reportsStore);

const reportId = computed(() => route.query.id as string);
const report = computed(() => reportsStore.getByReportId(reportId.value));
const reportCreatorId = computed(
  () => report.value?.created_by_user_id || null,
);

function onCopyReportLink() {
  copyToClipBoard(`${window.location.origin}/reports/${reportId.value}`);
  flashesStore.addSuccessFlash({
    message: 'Report link copied to clipboard',
  });
}

function getUser(userId: number) {
  if (currentUser.value?.id === userId) {
    return currentUser.value;
  }
  return teamStore.getAuthorizationByUserId(userId)?.user as User;
}
const existingUsersInReport = computed<UserWithReportAccess[]>(() => {
  if (!report.value?.user_access) return [];
  return report.value.user_access.map(
    (userAccess) =>
      ({
        ...userAccess,
        ...getUser(userAccess.user_id),
      }) as UserWithReportAccess,
  );
});

const folderId = computed(() => route.params.folder_id);
const closeRoute = computed(() => {
  if (!folderId.value) return { name: 'reports' };
  return {
    name: 'folder',
    params: { folder_id: folderId.value },
  };
});
async function onCloseModal() {
  await router.push(closeRoute.value);
}
</script>

<style lang="pcss" scoped>
.bitts-callout:deep() .bitts-callout__text .bitts-callout__title {
  @apply font-normal;
}
.share-report-modal.o-bitts-modal.bitts-modal--data:deep() dialog {
  .o-bitts-modal__header {
    @apply p-16 border-none;
  }
  .o-bitts-modal__content {
    @apply py-16 pt-0 m-0;
  }
}

.report-access-selection-controls {
  @apply p-0;
}

.o-bitts-modal:deep() dialog .close-icon {
  top: 8px;
  right: 8px;
}
</style>
